/**
 * useWidgetSnapshots — hooks KPI summary pour widgets SMART sidebar.
 *
 * PHASE 3.0 UX Revolution (DECISIONS #167).
 *
 * D-WIDGET-CODE-MORT (Lot D, 2026-07-19) — purge : `useMarcheWidget`,
 * `extractSamsungPdm`, `useActivitiesWidget`, `useFlashWidget` et le hook
 * composé `useWidgetSnapshots()` n'avaient plus aucun appelant réel
 * (`WidgetSidebar` consomme les 3 hooks individuels ci-dessous + le W1 Marché
 * passe par `useMarcheKpiCards`). `useMarcheWidget` répliquait en plus un bug
 * de défaut canal `'all'` corrigé ailleurs — code mort qui portait un bug
 * corrigé était un piège pour un futur réutilisateur. Seuls 2 fichiers de
 * test pré-existants cassés (Rollup parse, cf TESTS-ROLLUP-PARSE-PREEXISTANT)
 * mockaient encore ces symboles — aucun composant réel ne les consommait.
 *
 * W2 Performance: usePerfWidget → visit-rate rate_pct
 * W4 DQ         : useDqWidget → GET /api/v1/data-quality/summary
 *   Post P3 (2026-07-25) : payload réduit à { week_iso, computed_at }.
 *   Le widget V4 utilise week_iso comme ancre pour useDaV2NationalSeries (V2).
 *   Les compteurs merch/pdv/anomalies legacy ont été retirés avec les surfaces
 *   UI qui les affichaient — plus aucune lecture legacy dans le path chaud.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchPerfKpi } from '@/modules/performance/lib/api';
import { fetchDqSummary } from '@/modules/data-quality/lib/api';
import type { PerfApiResponse, VisitRateData } from '@/modules/performance/types/performance.types';

export type { VisitRateData };

const STALE = 5 * 60 * 1000; // 5 min

// ── W2 Performance — Taux de visite ───────────────────────────────────────────

/**
 * Cadran RETAIL figé — ne suit pas les filtres du module (choix Hkm).
 *
 * ⚠ `pdv_type` DOIT être passé : omis, le backend défaute à 'all'
 * (performance/api/schemas.ts:36) que api/service.ts:200 traduit en `undefined`
 * → AUCUN filtre canal → le taux de visite melangeait les 23 merchs retail et
 * les 4 wholesale en une moyenne ponderee (le wholesale pesait 9,1 % des visites
 * W26). Chiffre mathematiquement valide, mais retail et wholesale = 2 equipes :
 * un cadran unique qui les moyenne ne parle d'aucune des deux.
 */
export function usePerfWidget() {
  return useQuery<PerfApiResponse<VisitRateData>>({
    queryKey: ['widget', 'performance', 'visit-rate', 'latest_week', 'retail'],
    queryFn: () => fetchPerfKpi<VisitRateData>('visit-rate', {
      period_preset: 'latest_week',
      pdv_type:      'retail',
    }),
    staleTime: STALE,
    retry: 1,
  });
}

// ── W3 Activités — Stats breakdown (total / active / completed) ───────────────
// Alimente le widget sidebar ACTIVITIES TRACKING (kpi2/kpi3 — remplace les '—'
// hardcodés). Endpoint /activities/stats → 1 requête groupBy backend.

// ── W4 Data Accuracy — ancre semaine (V2 series) ─────────────────────────────
// Post P3 : le payload back se réduit à { week_iso, computed_at }. Le widget
// utilise l'ancre pour cadrer sa fenêtre V2 (17 sem par défaut).

export function useDqWidget() {
  return useQuery({
    queryKey: ['widget', 'dq', 'summary'],
    queryFn: () => fetchDqSummary(),
    staleTime: STALE,
    retry: 1,
    select: (data) => ({
      weekIso: data.week_iso,
    }),
  });
}
